import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { goalTreeViewing } from '@goaltree/backend/convex/lib/viewingContext'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@goaltree/ui/components/alert-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@goaltree/ui/components/avatar'
import { SidebarInset } from '@goaltree/ui/components/sidebar'
import { cn } from '@goaltree/ui/lib/utils'
import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router'
import confetti from 'canvas-confetti'
import { useAction, useConvexAuth, useMutation, useQuery } from 'convex/react'
import {
  CalendarDays,
  ChevronRight,
  Columns3,
  GitBranch,
  Heart,
  ImageIcon,
  Info,
  Layers,
  LayoutList,
  MoreVertical,
  RefreshCw,
  Search,
  Share2,
  Snowflake,
  Sparkles,
  Star,
  Target,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react'
import { useTheme } from 'next-themes'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AppSidebar } from '@/components/app-sidebar'
import { BottomNav } from '@/components/bottom-nav'
import { ClampText } from '@/components/clamp-text'
import { CoachHeaderButton } from '@/components/coach-header-button'
import { useSavePrivateTemplate } from '@/components/coaching/coach-library'
import { GoalCoachStrip, useClearCoachMarkers } from '@/components/coaching/goal-coach-strip'
import GoalCalendar from '@/components/goal-calendar'
import { GoalDetailSheet } from '@/components/goal-detail-sheet'
import GoalFlow from '@/components/goal-flow'
import {
  FreezeGoalDialog,
  FrozenBanner,
  goalFreezeState,
  useGoalFreezeActions,
} from '@/components/goal-freeze'
import GoalKanban from '@/components/goal-kanban'
import { GoalQuickAdd } from '@/components/goal-quick-add'
import GoalTree from '@/components/goal-tree'
import { IconTip } from '@/components/icon-tip'
import { InlineEditor } from '@/components/inline-editor'
import { JourneyPanel } from '@/components/journey'
import { SkeletonPlan } from '@/components/list-skeletons'
import { MobileScreenHeader } from '@/components/mobile-screen-header'
import { PersistentSidebarProvider as SidebarProvider } from '@/components/persistent-sidebar-provider'
import { ReplanChangesSheet } from '@/components/replan-changes-sheet'
import { ReplanModal } from '@/components/replan-modal'
import { ShareGoalDialog } from '@/components/share-goal-dialog'
import { SiteHeader } from '@/components/site-header'
import { TrajectoryPanel } from '@/components/trajectory-panel'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { ACCENT_COLORS, useAccentTheme } from '@/hooks/use-accent-theme'
import { useGoalReplanStatus } from '@/hooks/use-goal-replan-status'
import { useGoalTree } from '@/hooks/use-goal-tree'
import { useGoals } from '@/hooks/use-goals'
import { useIsPro } from '@/hooks/use-is-pro'
import { useIsMobile } from '@/hooks/use-mobile'
import { useRenameNode } from '@/hooks/use-rename-node'
import { useReplanGoal } from '@/hooks/use-replan-goal'
import { useSaveAsTemplate } from '@/hooks/use-save-as-template'
import { useHeaderSubtitle } from '@/hooks/use-scroll-title'
import { useStickyHeightVar } from '@/hooks/use-stuck'
import { CATEGORY_COLORS, categoryTextColor, useCategoryFill } from '@/lib/category-color'
import { useReportViewing } from '@/lib/coach-call-store'
import { useIsCoaching } from '@/lib/coaching-mode'
import { todayKey } from '@/lib/day'
import { dismissNewGoalBanner, newGoalBannerPending } from '@/lib/first-run'
import { patchGoal, removeGoal } from '@/lib/goals-optimistic'
import { pushRecent } from '@/lib/search-recent'
import { openShareCard } from '@/lib/share-card'
import { useUploadCover } from '@/lib/upload-cover'
import { useHeaderStore } from '@/store/header.store'
import { useViewStore } from '@/store/view.store'
function GoalCompletedCelebration({
  isAllDone,
  goalId,
  isOwner,
}: {
  isAllDone: boolean
  goalId: string
  isOwner: boolean
}) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const prevRef = useRef<boolean | null>(null)
  useEffect(() => {
    if (prevRef.current === null) {
      prevRef.current = isAllDone
      return
    }
    if (isAllDone && !prevRef.current) {
      const end = Date.now() + 3000
      const colors = [fill(CATEGORY_COLORS.mind), '#3DA9FC', '#2dd4bf', '#FF5C8A', '#FFB020']
      const frame = () => {
        confetti({ particleCount: 6, angle: 60, spread: 55, origin: { x: 0 }, colors })
        confetti({ particleCount: 6, angle: 120, spread: 55, origin: { x: 1 }, colors })
        if (Date.now() < end) requestAnimationFrame(frame)
      }
      requestAnimationFrame(frame)
      if (isOwner)
        toast.success(t('shareCard.goalToast'), {
          duration: 10_000,
          action: {
            label: t('shareCard.shareShort'),
            onClick: () => openShareCard({ type: 'goal', ref: goalId }),
          },
        })
    }
    prevRef.current = isAllDone
  }, [isAllDone, fill, isOwner, goalId, t])
  return null
}

function titleFromSlug(slug: string): string {
  const words = slug.split('-').slice(0, -1)
  if (!words.length) return 'Goal'
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

export const Route = createFileRoute('/goals/$goalId')({
  head: ({ params }) => ({
    meta: [
      { title: `${titleFromSlug(params.goalId)} — Goaltree` },
      { name: 'robots', content: 'noindex, nofollow' },
    ],
  }),
  validateSearch: (
    search,
  ): { openMilestone?: string; focusTask?: string; focusNode?: string; addTask?: boolean } => ({
    openMilestone: typeof search.openMilestone === 'string' ? search.openMilestone : undefined,
    focusTask: typeof search.focusTask === 'string' ? search.focusTask : undefined,
    focusNode: typeof search.focusNode === 'string' ? search.focusNode : undefined,
    addTask: search.addTask === true || search.addTask === 'true' ? true : undefined,
  }),
  component: RouteComponent,
})

function nameInitials(name?: string | null): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

type PresenceUser = NonNullable<
  ReturnType<typeof useQuery<typeof api.nodes.getGoalPresence>>
>[number]

function GoalPresenceBar({
  presence,
  currentUserId,
}: {
  presence: PresenceUser[]
  currentUserId: string | undefined
}) {
  if (presence.length < 2) return null
  const sorted = [...presence].sort((a, b) =>
    a.userId === currentUserId ? 1 : b.userId === currentUserId ? -1 : 0,
  )
  return (
    <div className="flex items-center gap-1">
      {sorted.map((p) => (
        <div
          key={p.userId}
          className="relative"
          title={p.userId === currentUserId ? 'You' : (p.name ?? 'Unknown')}
        >
          <Avatar className="h-7 w-7 rounded-full ring-2 ring-background">
            {p.image && <AvatarImage src={p.image} alt={p.name ?? ''} />}
            <AvatarFallback className="text-[10px]">{nameInitials(p.name)}</AvatarFallback>
          </Avatar>
          <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-green-500 ring-1 ring-background" />
        </div>
      ))}
    </div>
  )
}

function CursorOverlay({
  presence,
  currentUserId,
}: {
  presence: PresenceUser[]
  currentUserId: string | undefined
}) {
  const others = presence.filter(
    (p) => p.userId !== currentUserId && p.cursorX != null && p.cursorY != null,
  )
  if (others.length === 0) return null
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden">
      {others.map((p) => (
        <div
          key={p.userId}
          className="absolute transition-all duration-75"
          style={{ left: `${(p.cursorX ?? 0) * 100}%`, top: `${(p.cursorY ?? 0) * 100}%` }}
        >
          <svg width="16" height="20" viewBox="0 0 16 20" fill="none" className="-translate-x-px">
            <path
              d="M0 0L0 14L4 10L7 17L9 16L6 9L11 9Z"
              fill="currentColor"
              className="text-foreground"
            />
            <path d="M0 0L0 14L4 10L7 17L9 16L6 9L11 9Z" stroke="white" strokeWidth="1.5" />
          </svg>
          <span className="absolute left-4 top-0 whitespace-nowrap rounded bg-foreground px-1.5 py-0.5 text-[11px] font-medium text-background shadow-sm">
            {p.name ?? 'Unknown'}
          </span>
        </div>
      ))}
    </div>
  )
}

type GoalTreeQueryResult = NonNullable<
  ReturnType<
    typeof useQuery<typeof import('@goaltree/backend/convex/_generated/api').api.nodes.getGoalTree>
  >
>

function useGoalFrozen(goalId: Id<'nodes'>) {
  const goals = useGoals()
  return goalFreezeState(goals?.find((g) => g._id === goalId)?.freezes)
}

// Goal-details action CTAs (coach · star · overflow menu), rendered in the top
// header bar beside search. Owns the detail sheet, delete + upgrade dialogs and
// the cover-generation handlers.
function GoalHeaderActions({
  tree,
  canEdit = true,
  isOwner = false,
  allDone = false,
  goalSlug,
  onReplan,
  replanPending = false,
  transparent = false,
  showSearch = false,
  showCoach = true,
  ownsSheet = true,
}: {
  tree: GoalTreeQueryResult
  canEdit?: boolean
  isOwner?: boolean
  allDone?: boolean
  goalSlug?: string
  onReplan?: () => void
  replanPending?: boolean
  transparent?: boolean
  showSearch?: boolean
  // Desktop already has the coach in the sidebar. The header CTA stays on the phone.
  showCoach?: boolean
  // This component mounts in both the mobile and desktop headers; only the
  // active viewport's instance listens for the header tap, so the shared
  // detail sheet doesn't open twice.
  ownsSheet?: boolean
}) {
  const { t } = useTranslation()
  const removeNode = useMutation(api.nodes.remove).withOptimisticUpdate((store, { nodeId }) =>
    removeGoal(store, nodeId),
  )
  const removeCover = useMutation(api.nodes.removeCoverImage).withOptimisticUpdate(
    (store, { goalId }) => {
      store.setQuery(api.nodes.getCoverUrl, { goalId }, null)
      const covers = store.getQuery(api.nodes.getGoalCoverUrls, {})
      if (covers)
        store.setQuery(
          api.nodes.getGoalCoverUrls,
          {},
          covers.filter((c) => c.goalId !== goalId),
        )
      patchGoal(store, goalId, { hasCover: false })
    },
  )
  const generateCover = useAction((api as any).goalImages.generateCover)
  const uploadCover = useUploadCover()
  const generateCoverVideo = useAction(api.visionBoardActions.generateVideo)
  const coverUrl = useQuery(api.nodes.getCoverUrl, { goalId: tree._id })
  const navigate = useNavigate()
  const saveAsTemplate = useSaveAsTemplate()
  const savePrivateTemplate = useSavePrivateTemplate()
  // A coach's saves (in coaching mode, or of a client's goal) go to their private Library.
  const savesPrivate = useIsCoaching() || !!tree.viewerIsCoach
  const [sheetOpen, setSheetOpen] = useState(false)
  const sheetSummary = (() => {
    const tasks = tree.milestones.flatMap((ms) =>
      ms.targets.flatMap((tg) => tg.tasks.map((tk) => ({ tk, ms }))),
    )
    const next = tasks.find(({ tk }) => tk.completedAt === null)
    return {
      done: tasks.filter(({ tk }) => tk.completedAt !== null).length,
      total: tasks.length,
      next: next
        ? {
            _id: next.tk._id,
            title: next.tk.title,
            milestoneTitle: next.ms.title,
            effort: next.tk.effort,
          }
        : null,
      milestones: tree.milestones.map((ms) => {
        const all = ms.targets.flatMap((tg) => tg.tasks)
        return {
          _id: ms._id as string,
          title: ms.title,
          done: all.filter((tk) => tk.completedAt !== null).length,
          total: all.length,
        }
      }),
    }
  })()
  const [dotMenuOpen, setDotMenuOpen] = useState(false)
  const [dotMenuPos, setDotMenuPos] = useState({ top: 0, right: 0 })
  const [shareOpen, setShareOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [generatingCover, setGeneratingCover] = useState(false)
  const [generatingCoverVideo, setGeneratingCoverVideo] = useState(false)
  const { isPro } = useIsPro()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState<'replan' | 'edit-goal'>('replan')
  const { frozen } = useGoalFrozen(tree._id)
  const freezeActions = useGoalFreezeActions(tree._id)
  const [freezeOpen, setFreezeOpen] = useState(false)
  const prevReplanPending = useRef(replanPending)

  useEffect(() => {
    if (prevReplanPending.current && !replanPending) setDotMenuOpen(false)
    prevReplanPending.current = replanPending
  }, [replanPending])

  // Tapping the goal header (outside the title) opens the detail sheet — only
  // the active viewport's instance listens, so the sheet doesn't open twice.
  useEffect(() => {
    if (!ownsSheet) return
    const open = () => setSheetOpen(true)
    window.addEventListener('open-goal-details', open)
    return () => window.removeEventListener('open-goal-details', open)
  }, [ownsSheet])

  async function handleGenerateCover() {
    if (!isPro) {
      setDotMenuOpen(false)
      setUpgradeReason('edit-goal')
      setUpgradeOpen(true)
      return
    }
    setDotMenuOpen(false)
    setGeneratingCover(true)
    try {
      await generateCover({ goalId: tree._id })
      toast.success('Cover generated!')
    } catch {
      toast.error('Failed to generate cover')
    } finally {
      setGeneratingCover(false)
    }
  }
  async function handleGenerateCoverVideo() {
    if (!isPro) {
      setDotMenuOpen(false)
      setUpgradeReason('edit-goal')
      setUpgradeOpen(true)
      return
    }
    setDotMenuOpen(false)
    setGeneratingCoverVideo(true)
    try {
      await generateCoverVideo({ goalId: tree._id })
      toast.success(t('common.coverVideoGenerated'))
    } catch (err) {
      const kind = (err as { data?: { kind?: string } })?.data?.kind
      if (kind === 'videoLimit') toast.error(t('visionboard.videoLimit', { count: 10 }))
      else if (kind === 'notPro') {
        setUpgradeReason('edit-goal')
        setUpgradeOpen(true)
      } else toast.error(t('visionboard.videoError'))
    } finally {
      setGeneratingCoverVideo(false)
    }
  }

  const triggerCls = cn(
    'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md transition-colors',
    transparent
      ? 'text-white/80 hover:bg-white/10 hover:text-white'
      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
  )

  return (
    <div className="flex items-center gap-0.5">
      {showCoach && <CoachHeaderButton transparent={transparent} />}
      {showSearch && (
        <IconTip label={t('nav.search')}>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('open-search'))}
            className={triggerCls}
            aria-label={t('nav.search')}
          >
            <Search className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </IconTip>
      )}
      {/* 3-dot menu */}
      <div className="relative flex items-center">
        <IconTip label={t('goalSheet.more')}>
          <button
            type="button"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect()
              setDotMenuPos({
                top: r.bottom + 4,
                right: window.innerWidth - r.right,
              })
              setDotMenuOpen((v) => !v)
            }}
            className={triggerCls}
            aria-label={t('goalSheet.more')}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </IconTip>
        {isOwner && goalSlug && (
          <ShareGoalDialog
            goalId={tree._id}
            goalSlug={goalSlug}
            isReadOnly={tree.isReadOnly}
            isPublic={tree.isPublic}
            open={shareOpen}
            onOpenChange={setShareOpen}
          />
        )}
        {dotMenuOpen &&
          createPortal(
            <>
              <div className="fixed inset-0 z-[70]" onClick={() => setDotMenuOpen(false)} />
              <div
                style={dotMenuPos}
                className="fixed z-[71] min-w-[180px] overflow-hidden rounded-lg border bg-popover p-1 shadow-md"
              >
                <button
                  type="button"
                  onClick={() => {
                    setDotMenuOpen(false)
                    setSheetOpen(true)
                  }}
                  className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                >
                  <Info className="h-3.5 w-3.5" />
                  {t('common.details')}
                </button>
                {canEdit && (
                  <button
                    type="button"
                    disabled={generatingCover}
                    onClick={handleGenerateCover}
                    className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
                  >
                    <ImageIcon
                      className={`h-3.5 w-3.5 ${generatingCover ? 'animate-pulse' : ''}`}
                    />
                    {generatingCover ? t('common.generatingCover') : t('common.generateCoverImage')}
                  </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    disabled={generatingCoverVideo}
                    onClick={handleGenerateCoverVideo}
                    className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
                  >
                    <Video
                      className={`h-3.5 w-3.5 ${generatingCoverVideo ? 'animate-pulse' : ''}`}
                    />
                    {generatingCoverVideo
                      ? t('common.generatingCoverVideo')
                      : t('common.generateCoverVideo')}
                  </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setDotMenuOpen(false)
                      void uploadCover(tree._id)
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {t('common.uploadCover')}
                  </button>
                )}
                {canEdit && coverUrl && (
                  <button
                    type="button"
                    onClick={async () => {
                      setDotMenuOpen(false)
                      await removeCover({ goalId: tree._id })
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    {t('common.removeCover')}
                  </button>
                )}
                {canEdit && onReplan && (
                  <button
                    type="button"
                    disabled={allDone || replanPending}
                    onClick={() => {
                      if (!isPro) {
                        setDotMenuOpen(false)
                        setUpgradeReason('replan')
                        setUpgradeOpen(true)
                        return
                      }
                      onReplan()
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${replanPending ? 'animate-spin' : ''}`} />
                    {t('common.replanGoal')}
                  </button>
                )}
                {isOwner && goalSlug && (
                  <button
                    type="button"
                    onClick={() => {
                      setDotMenuOpen(false)
                      setShareOpen(true)
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    {t('shareModal.share')}
                  </button>
                )}
                {isOwner && !allDone && (
                  <button
                    type="button"
                    onClick={() => {
                      setDotMenuOpen(false)
                      if (frozen) void freezeActions.unfreeze()
                      else setFreezeOpen(true)
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                  >
                    <Snowflake className="h-3.5 w-3.5 text-sky-500" />
                    {t(frozen ? 'goalFreeze.unfreeze' : 'goalFreeze.freeze')}
                  </button>
                )}
                {(isOwner || tree.viewerIsCoach) && (
                  <button
                    type="button"
                    onClick={() => {
                      setDotMenuOpen(false)
                      void (savesPrivate ? savePrivateTemplate : saveAsTemplate)(tree._id)
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                  >
                    <Layers className="h-3.5 w-3.5" />
                    {t(savesPrivate ? 'coachLibrary.saveAsTemplate' : 'templates.saveAsTemplate')}
                  </button>
                )}
                {isOwner && (
                  <>
                    <div className="mx-3 my-1 border-t border-border/40" />
                    <button
                      type="button"
                      onClick={() => {
                        setDotMenuOpen(false)
                        setConfirmDelete(true)
                      }}
                      className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap rounded px-3 py-2 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {t('common.delete')}
                    </button>
                  </>
                )}
              </div>
            </>,
            document.body,
          )}
        {isOwner && (
          <FreezeGoalDialog goalId={tree._id} open={freezeOpen} onOpenChange={setFreezeOpen} />
        )}
      </div>

      <GoalDetailSheet
        goal={
          sheetOpen
            ? {
                _id: tree._id,
                title: tree.title,
                category: tree.category,
                status: tree.status,
                canEdit,
                isOwner,
                goalSlug,
                isReadOnly: tree.isReadOnly,
                isPublic: tree.isPublic,
                coverUrl,
                summary: sheetSummary,
                timeBudget: tree.timeBudget,
                allDone,
                replanPending,
                generatingCover,
                generatingCoverVideo,
                onReplan: onReplan ?? undefined,
                onGenerateCover: handleGenerateCover,
                onGenerateCoverVideo: handleGenerateCoverVideo,
                onRemoveCover: () => removeCover({ goalId: tree._id }),
                onUploadCover: () => void uploadCover(tree._id),
                onDelete: isOwner ? () => setConfirmDelete(true) : undefined,
                // Event rather than ?focusNode= so tapping the same item again re-scrolls.
                // Wait for the sheet to close so its focus restore doesn't fight the scroll.
                onOpenTask: (taskId) => {
                  setSheetOpen(false)
                  setTimeout(
                    () =>
                      window.dispatchEvent(
                        new CustomEvent('goal-focus-node', { detail: { id: taskId } }),
                      ),
                    250,
                  )
                },
                onOpenMilestone: (milestoneId) => {
                  setSheetOpen(false)
                  setTimeout(
                    () =>
                      window.dispatchEvent(
                        new CustomEvent('goal-focus-node', { detail: { id: milestoneId } }),
                      ),
                    250,
                  )
                },
              }
            : null
        }
        onClose={() => setSheetOpen(false)}
      />
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.deleteGoalTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('common.deleteGoalDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                removeNode({ nodeId: tree._id }).catch(() => toast.error("Couldn't delete goal"))
                navigate({ to: '/goals' })
              }}
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason={upgradeReason} />
    </div>
  )
}

function SkeletonGoalHeader({ className }: { className?: string }) {
  return (
    <div className={className}>
      <div className="mb-1 flex h-7 items-center justify-between">
        <div className="h-2.5 w-16 animate-pulse rounded bg-muted" />
        <div className="size-4 animate-pulse rounded bg-muted/60" />
      </div>
      <div className="flex h-[38px] items-center">
        <div className="h-7 w-3/5 animate-pulse rounded-lg bg-muted" />
      </div>
      <div className="mt-3 flex h-4 items-center justify-between">
        <div className="h-2.5 w-24 animate-pulse rounded bg-muted/60" />
        <div className="h-2.5 w-8 animate-pulse rounded bg-muted/60" />
      </div>
      <div className="mt-3 h-[3px] rounded-full bg-muted" />
    </div>
  )
}

const barHost = (el: HTMLElement) => el.parentElement?.parentElement ?? null

function GoalDetailHeader({
  tree,
  canEdit = true,
  className,
  fullScreen = false,
  onStuckChange,
}: {
  tree: GoalTreeQueryResult
  canEdit?: boolean
  className?: string
  fullScreen?: boolean
  onStuckChange?: (stuck: boolean) => void
}) {
  const { t, i18n } = useTranslation()
  const { resolvedTheme } = useTheme()
  const rename = useRenameNode()
  const [titleEditing, setTitleEditing] = useState(false)
  const coverUrl = useQuery(api.nodes.getCoverUrl, { goalId: tree._id })
  const likers = useQuery(api.nodes.getGoalLikers, { goalId: tree._id })
  const likeCount = likers?.length ?? 0
  // The cover fades to the page background at the bottom. That reads well in dark
  // but looks harsh fading to white in light, so skip the fade in light theme.
  const coverFade = resolvedTheme !== 'light'
  const coverMask = coverFade
    ? {
        maskImage: 'linear-gradient(to bottom, black 78%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 78%, transparent 100%)',
      }
    : {}
  const prefs = useQuery(api.preferences.getMyPreferences)
  const isStarred = new Set(
    (prefs as { starredGoalIds?: string[] } | null | undefined)?.starredGoalIds ?? [],
  ).has(tree._id)
  const toggleStar = useMutation(api.preferences.toggleStarGoal).withOptimisticUpdate(
    (localStore, { goalId }) => {
      const current = localStore.getQuery(api.preferences.getMyPreferences, {})
      if (current === undefined) return
      const ids = (current as { starredGoalIds?: string[] }).starredGoalIds ?? []
      const next = ids.includes(goalId) ? ids.filter((id) => id !== goalId) : [...ids, goalId]
      localStore.setQuery(
        api.preferences.getMyPreferences,
        {},
        { ...current, starredGoalIds: next },
      )
    },
  )
  const startGoal = useMutation(api.nodes.setGoalStatus)
  const containerRef = useRef<HTMLDivElement>(null)
  // Cover can be an <img> or a <video>; both are HTMLElements for the parallax transform.
  const imgRef = useRef<HTMLElement | null>(null)
  const pendingStart =
    (tree.startMode === 'date' && !!tree.startDate && tree.startDate > todayKey()) ||
    (tree.startMode === 'unsure' && tree.status === 'backlog')

  useEffect(() => {
    if (!coverUrl || !containerRef.current) return
    const container = containerRef.current
    let scrollEl: HTMLElement | null = container.parentElement
    while (scrollEl && scrollEl !== document.body) {
      const overflow = window.getComputedStyle(scrollEl).overflowY
      if (overflow === 'auto' || overflow === 'scroll') break
      scrollEl = scrollEl.parentElement
    }
    const target = scrollEl && scrollEl !== document.body ? scrollEl : window
    const handleScroll = () => {
      if (!imgRef.current) return
      const top = container.getBoundingClientRect().top
      imgRef.current.style.transform = `translateY(${-top * 0.35}px)`
    }
    target.addEventListener('scroll', handleScroll, { passive: true })
    return () => target.removeEventListener('scroll', handleScroll)
  }, [coverUrl])
  const allTasks = tree.milestones.flatMap((ms) => ms.targets.flatMap((tg) => tg.tasks))
  const completedCount = allTasks.filter((t) => t.completedAt !== null).length
  const pct = allTasks.length > 0 ? Math.round((completedCount / allTasks.length) * 100) : 0
  const color = CATEGORY_COLORS[tree.category.toLowerCase()] ?? '#6b7280'
  const isAllDone = allTasks.length > 0 && completedCount === allTasks.length

  // Collapse to a slim sticky bar (title + progress) once the header scrolls away.
  const barRef = useRef<HTMLDivElement>(null)
  useStickyHeightVar(barRef, '--goal-bar-h', barHost)
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { rootMargin: '-80px 0px 0px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    onStuckChange?.(stuck)
  }, [stuck, onStuckChange])
  useEffect(() => () => onStuckChange?.(false), [onStuckChange])
  // Once stuck, the title and count ride in the app header; the bar keeps only progress.
  const setScrolled = useHeaderStore((s) => s.setScrolled)
  useEffect(() => {
    setScrolled(stuck)
    return () => setScrolled(false)
  }, [stuck, setScrolled])
  useHeaderSubtitle(stuck ? `${completedCount}/${allTasks.length} · ${pct}%` : undefined)

  return (
    <>
      {/* Zero-height so the bar overlays rather than shifting the page; its height is
          published on the shared ancestor so milestone/target rows stick below it. */}
      <div
        className={cn('sticky top-(--app-hdr-h) z-30 h-0 overflow-visible', !fullScreen && '-mx-4')}
      >
        <div
          ref={barRef}
          aria-hidden={!stuck}
          className={cn(
            'bg-background px-4 pb-1.5 transition-[transform,opacity] duration-200',
            // Over a cover the transparent app headers float on top — leave room for them.
            fullScreen
              ? 'pt-[calc(env(safe-area-inset-top,0px)+3.75rem)] lg:pt-[3.25rem]'
              : 'pt-0.5',
            stuck ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0',
          )}
        >
          <div className="mx-auto w-full max-w-3xl">
            <div className="h-[3px] overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{ width: `${pct}%`, backgroundColor: 'var(--primary)' }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* The whole header opens the goal panel; the title (rename), star and other
          buttons stop propagation and keep their own actions. */}
      <div
        ref={containerRef}
        onClick={() => window.dispatchEvent(new CustomEvent('open-goal-details'))}
        className={cn(
          'group/cover relative cursor-pointer',
          coverUrl && !fullScreen && 'overflow-hidden rounded-2xl',
          !coverUrl && 'rounded-2xl transition-colors [@media(hover:hover)]:hover:bg-muted/25',
          className,
        )}
      >
        {coverUrl && (
          <div
            className={cn(
              'absolute inset-0 overflow-hidden pointer-events-none',
              !fullScreen && 'rounded-2xl',
            )}
          >
            {tree.coverIsVideo ? (
              <video
                ref={(el) => {
                  imgRef.current = el
                }}
                src={coverUrl}
                autoPlay
                muted
                loop
                playsInline
                className="absolute left-0 w-full object-cover object-[center_25%] transition-[scale,filter] duration-300 group-active/cover:brightness-90 [@media(hover:hover)]:group-hover/cover:scale-[1.02] [@media(hover:hover)]:group-hover/cover:brightness-110"
                style={{
                  top: '-8%',
                  height: '116%',
                  willChange: 'transform',
                  ...coverMask,
                }}
              />
            ) : (
              <img
                ref={(el) => {
                  imgRef.current = el
                }}
                src={coverUrl}
                alt=""
                className="absolute left-0 w-full object-cover object-[center_25%] transition-[scale,filter] duration-300 group-active/cover:brightness-90 [@media(hover:hover)]:group-hover/cover:scale-[1.02] [@media(hover:hover)]:group-hover/cover:brightness-110"
                style={{
                  top: '-8%',
                  height: '116%',
                  willChange: 'transform',
                  ...coverMask,
                }}
              />
            )}
            {/* Light scrim up top for the floating header controls; the text gets a
                bottom-weighted one so the picture itself stays clear. */}
            <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/45 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          </div>
        )}
        <div
          className={cn(
            'relative z-10',
            // fullScreen ⟺ cover layout: apply the padding immediately (not gated on
            // the async cover-image URL) so the header doesn't shift when the image
            // loads. While a first
            // cover is still generating (no coverUrl yet) the header stays plain —
            // no boxed container.
            fullScreen
              ? 'mx-auto flex min-h-[clamp(240px,32vh,340px)] w-full max-w-3xl flex-col justify-end px-4 pb-4 pt-[calc(env(safe-area-inset-top,0px)+4rem)]'
              : coverUrl && 'px-5 pt-5 pb-4',
          )}
          style={
            coverUrl
              ? ({
                  '--foreground': 'oklch(1 0 0)',
                  '--muted-foreground': 'oklch(0.8 0 0)',
                } as React.CSSProperties)
              : undefined
          }
        >
          <div>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest">
                    <p style={{ color: categoryTextColor(color) }}>
                      {t(`goals.cat.${tree.category.toLowerCase()}`, {
                        defaultValue: tree.category,
                      })}
                    </p>
                    {likeCount > 0 && (
                      <span className="flex items-center gap-0.5 text-muted-foreground">
                        <span className="text-muted-foreground/50">・</span>
                        <Heart className="h-2.5 w-2.5 shrink-0" />
                        <span className="leading-none">{likeCount}</span>
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleStar({ goalId: tree._id })
                    }}
                    className="-mr-1 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/50"
                    aria-label={isStarred ? t('sidebar.unstar') : t('sidebar.star')}
                  >
                    <Star
                      className={cn(
                        'h-4 w-4 transition-colors',
                        isStarred && 'fill-yellow-400 text-yellow-400',
                      )}
                    />
                  </button>
                </div>
                {/* Title stays editable; taps here shouldn't open the detail sheet. */}
                <ClampText off={titleEditing} className="cursor-text">
                  {(clamp) => (
                    <div onClick={(e) => e.stopPropagation()}>
                      <InlineEditor
                        value={tree.title}
                        onSave={(t) => rename(tree._id, t)}
                        onEditingChange={setTitleEditing}
                        disabled={!canEdit}
                        className={cn(
                          'text-3xl font-black leading-tight text-foreground',
                          isAllDone && 'line-through opacity-60',
                          clamp,
                        )}
                      />
                    </div>
                  )}
                </ClampText>
                {tree.description && (
                  <ClampText>
                    {(clamp) => (
                      <p className={cn('mt-1.5 text-sm text-muted-foreground', clamp)}>
                        {tree.description}
                      </p>
                    )}
                  </ClampText>
                )}
                {pendingStart && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      {tree.startMode === 'date' && tree.startDate
                        ? t('goals.startsOn', {
                            date: new Date(`${tree.startDate}T00:00:00`).toLocaleDateString(
                              i18n.language,
                              { month: 'short', day: 'numeric', year: 'numeric' },
                            ),
                          })
                        : t('goals.startPending')}
                    </span>
                    {tree.isOwner && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          startGoal({ goalId: tree._id, status: 'in-progress' }).catch(() =>
                            toast.error(t('common.error')),
                          )
                        }}
                        className="cursor-pointer rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
                      >
                        {t('goals.startNow')}
                      </button>
                    )}
                  </div>
                )}
                {!tree.isOwner && tree.ownerName && (
                  <div className="mt-2 flex items-center gap-2">
                    <Avatar className="h-5 w-5 shrink-0 rounded-full">
                      <AvatarImage src={tree.ownerImage} alt={tree.ownerName} />
                      <AvatarFallback className="text-[9px]">
                        {nameInitials(tree.ownerName)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-muted-foreground">
                      by <span className="font-medium text-foreground">{tree.ownerName}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
            {allTasks.length > 0 && (
              <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>
                  {isAllDone
                    ? t('goals.allTasksComplete')
                    : t('goals.taskCount', { completed: completedCount, total: allTasks.length })}
                </span>
                <span className="flex shrink-0 items-center gap-3">
                  <span className="hidden items-center gap-0.5 font-semibold text-foreground opacity-0 transition-opacity duration-200 group-hover/cover:opacity-100 [@media(hover:hover)]:flex">
                    {t('common.details')}
                    <ChevronRight className="size-3.5" />
                  </span>
                  <span className="font-bold text-primary-text">{pct}%</span>
                </span>
              </div>
            )}
            {allTasks.length > 0 && (
              <div
                className={cn(
                  'relative mt-3 h-[3px] rounded-full',
                  coverUrl ? 'bg-white/20' : 'bg-muted',
                )}
              >
                <div
                  className="absolute left-0 top-0 h-full rounded-full transition-[width] duration-500"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: 'var(--primary)',
                    boxShadow: '0 0 8px var(--primary)',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// One-time "You're set" card on a just-created goal: bridges to Today.
function NewGoalBanner({ goalId }: { goalId: Id<'nodes'> }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(() => newGoalBannerPending(goalId))
  const today = useQuery(
    api.dailySurface.getTodayWithContext,
    open ? { localDate: todayKey() } : 'skip',
  )
  if (!open) return null
  const inToday =
    today?.tasks.filter((task) => task.goalId === goalId && task.completedAt === null).length ?? 0
  function close() {
    dismissNewGoalBanner(goalId)
    setOpen(false)
  }
  return (
    <div className="fixed inset-x-3 bottom-20 z-40 flex items-center gap-3 rounded-2xl border bg-card p-3.5 shadow-lg animate-in fade-in slide-in-from-bottom-4 lg:right-6 lg:bottom-6 lg:left-auto lg:w-[380px]">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary-text">
        <Sparkles className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold">{t('goals.newGoal.title')}</p>
        <p className="text-xs text-muted-foreground">
          {inToday > 0 ? t('goals.newGoal.inToday', { count: inToday }) : t('goals.newGoal.none')}
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          close()
          navigate({ to: '/today' })
        }}
        className="shrink-0 cursor-pointer rounded-full bg-primary px-3.5 py-1.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {t('goals.newGoal.goToday')}
      </button>
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={close}
        className="shrink-0 cursor-pointer rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

function GoalView({
  goalId,
  goalSlug,
  openMilestone,
  focusTask,
  focusNode,
}: {
  goalId: Id<'nodes'>
  goalSlug: string
  openMilestone?: string
  focusTask?: string
  focusNode?: string
}) {
  const { t } = useTranslation()
  const rawView = useViewStore((s) => s.view)
  const isMobile = useIsMobile()
  // Mobile mirrors the inbox: list + calendar only. Kanban/flow stay desktop.
  const view = isMobile ? (rawView === 'calendar' ? 'calendar' : 'list') : rawView
  const setView = useViewStore((s) => s.setView)
  const { isPro } = useIsPro()
  const [calUpgradeOpen, setCalUpgradeOpen] = useState(false)
  const navigate = useNavigate()
  const updatePresence = useMutation(api.nodes.updatePresence)
  const tree = useGoalTree(goalId)
  useClearCoachMarkers(tree)
  useReportViewing(() => (tree?.isOwner ? goalTreeViewing(tree._id, tree) : null))
  const { frozen, until: frozenUntil } = useGoalFrozen(goalId)
  // Opened goals feed the ⌘K palette's "Recent" list.
  useEffect(() => {
    if (tree?.isOwner) pushRecent(tree._id)
  }, [tree?._id, tree?.isOwner])
  // Layout keys off the tree's own cover flag (loads with the tree) rather than a
  // separate cover-URL query — otherwise the layout flashes/jerks when that
  // resolves. The header still fetches the image URL for display.
  const hasCover = tree?.hasCover === true
  // The calendar renders the full-bleed cover like the other views; its sticky
  // toolbar is offset below the transparent nav (GoalCalendar coverMode).
  // Once the sticky bar covers the media, header controls switch to theme colors.
  const [headerStuck, setHeaderStuck] = useState(false)
  const coverActive = hasCover && !headerStuck
  const { accent } = useAccentTheme()
  const coaching = useIsCoaching()
  const fill = useCategoryFill()
  const presenceData = useQuery(api.nodes.getGoalPresence, { goalId }) ?? []
  const currentUser = useQuery(api.auth.getCurrentUser)
  const replanStatus = useGoalReplanStatus(goalId)
  const triggerReplan = useReplanGoal()
  const [replanModalOpen, setReplanModalOpen] = useState(false)
  const [replanChangesOpen, setReplanChangesOpen] = useState(false)
  const [replanOpenMilestoneId, setReplanOpenMilestoneId] = useState<string | undefined>()
  function clearOpenMilestone() {
    if (!openMilestone && !focusTask && !focusNode) return
    navigate({
      to: '/goals/$goalId',
      params: { goalId: goalSlug },
      search: { openMilestone: undefined, focusTask: undefined, focusNode: undefined },
      replace: true,
    })
  }
  const prevReplanPending = useRef<boolean | null>(null)
  const throttleRef = useRef<number>(0)
  const isOwner = tree?.isOwner ?? false
  const canEdit = tree?.canEdit ?? false
  const allTasks = tree?.milestones.flatMap((ms) => ms.targets.flatMap((tg) => tg.tasks))
  const isAllDone = allTasks
    ? allTasks.length > 0 && allTasks.every((task) => task.completedAt !== null)
    : null
  useEffect(() => {
    if (!replanStatus) return
    if (
      prevReplanPending.current === true &&
      replanStatus.pending === false &&
      replanStatus.changes
    ) {
      setReplanModalOpen(true)
    }
    prevReplanPending.current = replanStatus.pending
  }, [replanStatus])
  useEffect(() => {
    if (!tree || accent === 'mono' || coaching) return
    // Tint the OS status bar with the current accent while on a goal. index.html
    // ships TWO theme-color metas (dark + light, each media-scoped), so set ALL
    // of them — otherwise the untouched one keeps a solid bar.
    const color = fill(ACCENT_COLORS[accent] ?? CATEGORY_COLORS.mind)
    const metas = Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'))
    if (metas.length === 0) {
      const m = document.createElement('meta')
      m.name = 'theme-color'
      m.content = color
      document.head.appendChild(m)
      return () => m.remove()
    }
    const prev = metas.map((m) => m.content)
    for (const m of metas) m.content = color
    return () => {
      metas.forEach((m, i) => {
        m.content = prev[i] ?? '#0a0a0a'
      })
    }
  }, [tree, accent, fill, coaching])
  useEffect(() => {
    updatePresence({ goalId })
    const interval = setInterval(() => updatePresence({ goalId }), 15_000)
    return () => clearInterval(interval)
  }, [goalId, updatePresence])
  const othersPresent = presenceData.some((p) => p.userId !== currentUser?._id)
  useEffect(() => {
    if (!othersPresent) return
    function handleMouseMove(e: MouseEvent) {
      const now = Date.now()
      if (now - throttleRef.current < 1000) return
      throttleRef.current = now
      updatePresence({
        goalId,
        cursorX: e.clientX / window.innerWidth,
        cursorY: e.clientY / window.innerHeight,
      })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [goalId, updatePresence, othersPresent])
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset
        className={cn(
          'flex h-svh flex-col overflow-hidden bg-background',
          (hasCover || frozen) && 'relative',
        )}
      >
        {frozen && (
          // Icy frame around the whole page so a paused goal reads as paused.
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-30 bg-sky-300/[0.04] shadow-[inset_0_0_0_2px_rgb(125_211_252/0.6),inset_0_0_120px_rgb(125_211_252/0.28)]"
          />
        )}
        <CursorOverlay presence={presenceData} currentUserId={currentUser?._id} />
        {tree?.isOwner && <NewGoalBanner goalId={tree._id} />}
        <MobileScreenHeader
          title={tree?.title ?? ''}
          onBack={() => window.history.back()}
          showInbox={false}
          showSearch={false}
          tightBack
          col={view === 'list' ? '48rem' : '100%'}
          transparent={coverActive}
          overlay={hasCover}
          actions={
            tree ? (
              // -mr-1.5 pulls the action cluster to the edge so the 3-dot menu
              // lines up vertically with the star below it (which sits at -mr-1).
              <div className="-mr-1.5 flex items-center gap-1">
                <div
                  className={cn(
                    'flex items-center divide-x overflow-hidden rounded-md border',
                    coverActive ? 'divide-white/20 border-white/20' : 'divide-border border-border',
                  )}
                >
                  {(
                    [
                      { value: 'list', icon: <LayoutList className="h-4 w-4" />, pro: false },
                      { value: 'calendar', icon: <CalendarDays className="h-4 w-4" />, pro: true },
                    ] as const
                  ).map(({ value, icon, pro }) => {
                    const active = value === 'calendar' ? view === 'calendar' : view !== 'calendar'
                    return (
                      <IconTip
                        key={value}
                        label={t(value === 'calendar' ? 'goals.viewCalendar' : 'goals.viewList')}
                      >
                        <button
                          type="button"
                          aria-label={t(
                            value === 'calendar' ? 'goals.viewCalendar' : 'goals.viewList',
                          )}
                          onClick={() => {
                            if (pro && !isPro) {
                              setCalUpgradeOpen(true)
                              return
                            }
                            setView(value)
                          }}
                          className={cn(
                            'flex h-8 w-8 cursor-pointer items-center justify-center transition-colors',
                            coverActive
                              ? active
                                ? 'bg-white/20 text-white'
                                : 'text-white/60 hover:bg-white/10 hover:text-white'
                              : active
                                ? 'bg-muted text-foreground'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          )}
                        >
                          {icon}
                        </button>
                      </IconTip>
                    )
                  })}
                </div>
                <GoalHeaderActions
                  tree={tree}
                  canEdit={canEdit}
                  isOwner={isOwner}
                  allDone={isAllDone ?? false}
                  goalSlug={goalSlug}
                  onReplan={() => triggerReplan({ goalId })}
                  replanPending={replanStatus?.pending === true}
                  transparent={coverActive}
                  showSearch
                  ownsSheet={isMobile}
                />
              </div>
            ) : undefined
          }
        />
        <SiteHeader
          showBack
          transparent={coverActive}
          overlay={hasCover}
          className="hidden lg:flex"
          leftSlot={<GoalPresenceBar presence={presenceData} currentUserId={currentUser?._id} />}
          rightSlot={
            <div className="flex items-center gap-1">
              <div
                className={cn(
                  'flex items-center divide-x rounded-md border overflow-hidden',
                  coverActive ? 'divide-white/20 border-white/20' : 'divide-border border-border',
                )}
              >
                {(
                  [
                    {
                      value: 'list',
                      icon: <LayoutList className="h-3.5 w-3.5" />,
                      label: t('goals.viewList'),
                      pro: false,
                    },
                    {
                      value: 'kanban',
                      icon: <Columns3 className="h-3.5 w-3.5" />,
                      label: t('goals.viewKanban'),
                      pro: false,
                    },
                    {
                      value: 'flow',
                      icon: <GitBranch className="h-3.5 w-3.5" />,
                      label: t('goals.viewGraph'),
                      pro: false,
                    },
                    {
                      value: 'calendar',
                      icon: <CalendarDays className="h-3.5 w-3.5" />,
                      label: t('goals.viewCalendar'),
                      pro: true,
                    },
                  ] as const
                ).map(({ value, icon, label, pro }) => (
                  <IconTip key={value} label={label}>
                    <button
                      type="button"
                      aria-label={label}
                      onClick={() => {
                        if (pro && !isPro) {
                          setCalUpgradeOpen(true)
                          return
                        }
                        setView(value)
                        clearOpenMilestone()
                      }}
                      className={cn(
                        'cursor-pointer flex h-8 w-8 items-center justify-center transition-colors',
                        coverActive
                          ? view === value
                            ? 'bg-white/20 text-white'
                            : 'text-white/60 hover:bg-white/10 hover:text-white'
                          : view === value
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                      )}
                    >
                      {icon}
                    </button>
                  </IconTip>
                ))}
              </div>
              {tree && (
                <GoalHeaderActions
                  tree={tree}
                  canEdit={canEdit}
                  isOwner={isOwner}
                  allDone={isAllDone ?? false}
                  goalSlug={goalSlug}
                  onReplan={() => triggerReplan({ goalId })}
                  replanPending={replanStatus?.pending === true}
                  transparent={coverActive}
                  showCoach={false}
                  ownsSheet={!isMobile}
                />
              )}
            </div>
          }
        />
        {frozen && tree?.isOwner && (
          <div
            className={cn(
              'z-20 px-4 pt-2 pb-1',
              // Over a cover the header floats and publishes no height, so the
              // banner floats just under it instead of taking the header's place.
              hasCover
                ? 'absolute inset-x-0 top-[calc(env(safe-area-inset-top,0px)+3.5rem)] lg:top-[3.25rem]'
                : 'relative mt-(--app-hdr-h)',
            )}
          >
            <div className={cn('mx-auto max-w-3xl', hasCover && 'rounded-xl bg-background')}>
              <FrozenBanner goalId={goalId} until={frozenUntil} />
            </div>
          </div>
        )}
        <div
          data-hdr-skip={frozen && tree?.isOwner && !hasCover ? '' : undefined}
          className={cn(
            // isolate: sticky rows' z-indexes stay under the floating app header.
            view === 'flow'
              ? 'h-[calc(100vh-110px)] lg:h-[calc(100vh-50px)] min-h-0 flex'
              : 'isolate flex-1 overflow-y-auto pb-16 lg:pb-0',
            view === 'flow' && !(frozen && tree?.isOwner && !hasCover) && 'mt-(--app-hdr-h)',
            // Paused: the plan reads as on ice (still usable).
            frozen && 'opacity-70 saturate-[0.35] transition-[opacity,filter] duration-700',
          )}
        >
          {tree === undefined ? (
            view === 'flow' ? (
              <div className="flex min-h-0 flex-1 items-center justify-center">
                <svg
                  width="280"
                  height="190"
                  className="animate-pulse text-muted-foreground/30"
                  aria-hidden="true"
                >
                  {/* root */}
                  <rect x="110" y="14" width="60" height="26" rx="5" fill="currentColor" />
                  {/* lines root → L2 */}
                  <line x1="140" y1="40" x2="48" y2="86" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="140" y1="40" x2="140" y2="86" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="140" y1="40" x2="232" y2="86" stroke="currentColor" strokeWidth="1.5" />
                  {/* L2 */}
                  <rect x="18" y="86" width="60" height="24" rx="5" fill="currentColor" />
                  <rect x="110" y="86" width="60" height="24" rx="5" fill="currentColor" />
                  <rect x="202" y="86" width="60" height="24" rx="5" fill="currentColor" />
                  {/* lines L2 → L3 */}
                  <line x1="48" y1="110" x2="36" y2="148" stroke="currentColor" strokeWidth="1" />
                  <line x1="48" y1="110" x2="72" y2="148" stroke="currentColor" strokeWidth="1" />
                  <line x1="140" y1="110" x2="140" y2="148" stroke="currentColor" strokeWidth="1" />
                  <line x1="232" y1="110" x2="208" y2="148" stroke="currentColor" strokeWidth="1" />
                  <line x1="232" y1="110" x2="244" y2="148" stroke="currentColor" strokeWidth="1" />
                  {/* L3 */}
                  <rect x="10" y="148" width="52" height="20" rx="4" fill="currentColor" />
                  <rect x="46" y="148" width="52" height="20" rx="4" fill="currentColor" />
                  <rect x="114" y="148" width="52" height="20" rx="4" fill="currentColor" />
                  <rect x="182" y="148" width="52" height="20" rx="4" fill="currentColor" />
                  <rect x="218" y="148" width="52" height="20" rx="4" fill="currentColor" />
                </svg>
              </div>
            ) : view === 'kanban' ? (
              <div className="px-4 pt-8 pb-4">
                {/* header skeleton */}
                <SkeletonGoalHeader className="mb-6" />
                {/* milestone label */}
                <div className="mb-3 h-4 w-40 animate-pulse rounded bg-muted" />
                {/* 3 kanban columns */}
                <div className="flex gap-3">
                  {[3, 2, 1].map((count, ci) => (
                    <div key={ci} className="w-[410px] shrink-0">
                      <div className="mb-2 flex items-center gap-1.5">
                        <div className="h-2.5 w-16 animate-pulse rounded bg-muted" />
                        <div className="h-2.5 w-4 animate-pulse rounded bg-muted" />
                      </div>
                      <div className="flex flex-col gap-1.5 rounded-lg border border-dashed border-border/60 p-2">
                        {Array.from({ length: count }).map((_, i) => (
                          <div
                            key={i}
                            className="rounded-xl border border-border/60 bg-card px-3 py-2.5"
                          >
                            <div
                              className="h-4 animate-pulse rounded bg-muted"
                              style={{ width: `${55 + i * 12}%` }}
                            />
                            <div className="mt-1 flex items-center gap-1.5">
                              <div className="h-2.5 w-24 animate-pulse rounded bg-muted" />
                              <div className="ml-auto h-2.5 w-10 animate-pulse rounded bg-muted shrink-0" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mx-auto max-w-3xl px-4 pt-6 pb-6">
                <SkeletonGoalHeader className="mb-8" />
                <SkeletonPlan upNext />
              </div>
            )
          ) : tree === null ? (
            <div className="flex flex-1 flex-col items-center justify-center px-4 py-16 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
                <Target className="h-8 w-8 text-muted-foreground/50" />
              </div>
              <h2 className="text-xl font-bold">{t('goals.notFound')}</h2>
              <p className="mt-2 max-w-xs text-sm text-muted-foreground">
                {t('goals.notFoundDesc')}
              </p>
              <button
                type="button"
                onClick={() => navigate({ to: '/goals' })}
                className="cursor-pointer mt-6 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white dark:text-[#0a0a0a] transition-colors hover:bg-primary/90"
              >
                {t('goals.backToGoals')}
              </button>
            </div>
          ) : view === 'calendar' ? (
            hasCover ? (
              <>
                <GoalDetailHeader
                  tree={tree}
                  canEdit={canEdit}
                  onStuckChange={setHeaderStuck}
                  className=""
                  fullScreen
                />
                <GoalCalendar tree={tree} coverMode />
              </>
            ) : (
              <>
                <div className="px-4 pt-8">
                  <GoalDetailHeader
                    tree={tree}
                    canEdit={canEdit}
                    onStuckChange={setHeaderStuck}
                    className="mb-4"
                  />
                </div>
                <GoalCalendar tree={tree} />
              </>
            )
          ) : view === 'flow' ? (
            <GoalFlow tree={tree} />
          ) : view === 'kanban' ? (
            hasCover ? (
              <>
                <GoalDetailHeader
                  tree={tree}
                  canEdit={canEdit}
                  onStuckChange={setHeaderStuck}
                  className=""
                  fullScreen
                />
                <div className="px-4 pb-4 pt-6">
                  <GoalKanban
                    tree={tree}
                    canEdit={canEdit}
                    openMilestoneId={replanOpenMilestoneId ?? openMilestone}
                  />
                </div>
              </>
            ) : (
              <div className="px-4 pt-8 pb-4">
                <GoalDetailHeader
                  tree={tree}
                  canEdit={canEdit}
                  onStuckChange={setHeaderStuck}
                  className="mx-auto mb-6 max-w-3xl"
                />
                <GoalKanban
                  tree={tree}
                  canEdit={canEdit}
                  openMilestoneId={replanOpenMilestoneId ?? openMilestone}
                />
              </div>
            )
          ) : hasCover ? (
            <>
              <GoalDetailHeader
                tree={tree}
                canEdit={canEdit}
                onStuckChange={setHeaderStuck}
                className=""
                fullScreen
              />
              <div className="mx-auto max-w-3xl px-4 pb-4 pt-6">
                <GoalCoachStrip tree={tree} className="mb-4" />
                {canEdit && !tree.isInbox && <GoalQuickAdd goalId={tree._id} className="mb-4" />}
                <TrajectoryPanel goalId={tree._id} className="mb-4" />
                {isOwner && !tree.isInbox && <JourneyPanel goalId={tree._id} className="mb-4" />}
                <GoalTree
                  tree={tree}
                  canEdit={canEdit}
                  viewerIsCoach={tree.viewerIsCoach}
                  openMilestoneId={replanOpenMilestoneId ?? openMilestone}
                  focusTaskId={focusTask}
                  focusNodeId={focusNode}
                  onAccordionChange={clearOpenMilestone}
                />
              </div>
            </>
          ) : (
            <div className="mx-auto max-w-3xl px-4 pt-6 pb-6">
              <GoalDetailHeader
                tree={tree}
                canEdit={canEdit}
                onStuckChange={setHeaderStuck}
                className="mb-8"
              />
              <GoalCoachStrip tree={tree} className="mb-4" />
              {canEdit && !tree.isInbox && <GoalQuickAdd goalId={tree._id} className="mb-4" />}
              <TrajectoryPanel goalId={tree._id} className="mb-4" />
              {isOwner && !tree.isInbox && <JourneyPanel goalId={tree._id} className="mb-4" />}
              <div>
                <GoalTree
                  tree={tree}
                  canEdit={canEdit}
                  viewerIsCoach={tree.viewerIsCoach}
                  openMilestoneId={replanOpenMilestoneId ?? openMilestone}
                  focusTaskId={focusTask}
                  focusNodeId={focusNode}
                  onAccordionChange={clearOpenMilestone}
                />
              </div>
            </div>
          )}
        </div>
        {replanStatus?.changes && tree && (
          <ReplanModal
            open={replanModalOpen}
            onClose={() => setReplanModalOpen(false)}
            onSeeChanges={() => {
              setReplanModalOpen(false)
              if (replanStatus.richChanges) {
                setReplanChangesOpen(true)
              } else {
                setView('list')
                const activeMilestone = tree.milestones.find(
                  (ms) => ms.completedAt === null && ms.progress < 100,
                )
                if (activeMilestone) setReplanOpenMilestoneId(activeMilestone._id)
              }
            }}
            changes={replanStatus.changes}
            completedMilestones={
              tree.milestones.filter((ms) => ms.completedAt !== null || ms.progress === 100).length
            }
            totalMilestones={tree.milestones.length}
          />
        )}
        {replanStatus?.richChanges && tree && (
          <ReplanChangesSheet
            open={replanChangesOpen}
            onClose={() => setReplanChangesOpen(false)}
            goalTitle={tree.title}
            changes={replanStatus.richChanges}
          />
        )}
        {isAllDone !== null && tree && (
          // Key by goal so the "just completed" baseline resets per goal — otherwise
          // navigating from an incomplete goal to a completed one reads as a
          // false→true transition and re-fires the confetti.
          <GoalCompletedCelebration
            key={tree._id}
            isAllDone={isAllDone}
            goalId={tree._id}
            isOwner={isOwner}
          />
        )}
        <UpgradeDialog open={calUpgradeOpen} onOpenChange={setCalUpgradeOpen} reason="calendar" />
      </SidebarInset>
      <BottomNav />
    </SidebarProvider>
  )
}

function GoalViewDispatch({
  goalId,
  goalSlug,
  openMilestone,
  focusTask,
  focusNode,
  addTask: _addTask,
}: {
  goalId: Id<'nodes'>
  goalSlug: string
  openMilestone?: string
  focusTask?: string
  focusNode?: string
  addTask?: boolean
}) {
  const tree = useGoalTree(goalId)
  if (tree?.isInbox || goalSlug.startsWith('inbox-')) return <Navigate to="/everyday" replace />
  return (
    <GoalView
      goalId={goalId}
      goalSlug={goalSlug}
      openMilestone={openMilestone}
      focusTask={focusTask}
      focusNode={focusNode}
    />
  )
}

function RouteComponent() {
  const { t } = useTranslation()
  const { goalId: goalSlug } = Route.useParams()
  const { openMilestone, focusTask, focusNode, addTask } = Route.useSearch() as {
    openMilestone?: string
    focusTask?: string
    focusNode?: string
    addTask?: boolean
  }
  const { isAuthenticated, isLoading } = useConvexAuth()
  const navigate = useNavigate()
  const lastSegment = goalSlug.split('-').pop() ?? goalSlug
  const isValidId = lastSegment.length >= 20 && /^[a-z0-9]+$/.test(lastSegment)
  const convexId = lastSegment as Id<'nodes'>
  if (isLoading) {
    return (
      <div className="flex h-svh items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    )
  }
  if (!isAuthenticated) {
    navigate({ to: '/auth/login', search: { returnTo: `/goals/${goalSlug}` }, replace: true })
    return null
  }
  if (!isValidId) {
    return (
      <div className="flex h-svh flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-lg font-semibold">{t('goals.notFound')}</p>
        <p className="text-sm text-muted-foreground">{t('goals.notFoundDesc')}</p>
        <button
          type="button"
          onClick={() => navigate({ to: '/goals' })}
          className="cursor-pointer mt-4 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-white dark:text-[#0a0a0a] transition-colors hover:bg-primary/90"
        >
          {t('goals.backToGoals')}
        </button>
      </div>
    )
  }
  return (
    <GoalViewDispatch
      goalId={convexId}
      goalSlug={goalSlug}
      openMilestone={openMilestone}
      focusTask={focusTask}
      focusNode={focusNode}
      addTask={addTask}
    />
  )
}
